/**
 * Utility functions for working with Shopify product variants
 */

/**
 * Finds a variant by size or title.
 * @param {Array} variants - Array of Shopify variants
 * @param {string} targetVariantName - The size or title to find (e.g., "King", "Queen")
 * @returns {Object|null} The matching variant or null if not found
 */
export function findVariantByName(variants, targetVariantName) {
  if (!Array.isArray(variants) || !targetVariantName) {
    return null;
  }

  // First, try to find by exact title match
  let variant = variants.find((v) => v.title === targetVariantName);

  if (variant) {
    return variant;
  }

  // Then try to find by size option (most common for mattresses)
  variant = variants.find((v) =>
    v.selectedOptions?.some(
      (option) => option.name === 'Size' && option.value === targetVariantName
    )
  );

  if (variant) {
    return variant;
  }

  // Try partial match in title (e.g., "Queen / Light Grey" contains "Queen")
  variant = variants.find((v) =>
    v.title.toLowerCase().includes(targetVariantName.toLowerCase())
  );

  return variant || null;
}

/**
 * Gets the display price for a product based on product_variant_display setting.
 * Falls back to lowest price if specific variant not found or not specified.
 * @param {Array} variants - Array of Shopify variants
 * @param {string} productSizeDisplay - The variant name to display price for
 * @returns {Object} Object with variant, price, compareAtPrice, and displayFormat
 */
export function getDisplayPriceData(variants, productSizeDisplay) {
  if (!Array.isArray(variants) || !variants.length) {
    return {
      variant: null,
      price: null,
      compareAtPrice: null,
      displayFormat: 'from'
    };
  }

  // If product_variant_display is specified, try to find that variant
  if (productSizeDisplay) {
    const specificVariant = findVariantByName(variants, productSizeDisplay);

    if (specificVariant && specificVariant.availableForSale) {
      return {
        variant: specificVariant,
        price: specificVariant.price,
        compareAtPrice: specificVariant.compareAtPrice,
        displayFormat: 'specific',
        variantName: productSizeDisplay
      };
    }
  }

  // Fallback to lowest price logic
  const sortedVariants = [...variants]
    .filter((v) => v.availableForSale)
    .sort((a, b) => parseFloat(a.price.amount) - parseFloat(b.price.amount));

  const lowestPriceVariant = sortedVariants[0] || variants[0];

  return {
    variant: lowestPriceVariant,
    price: lowestPriceVariant.price,
    compareAtPrice: lowestPriceVariant.compareAtPrice,
    displayFormat: 'from'
  };
}

/**
 * Formats the price label based on display format
 * @param {string} displayFormat - 'from' or 'specific'
 * @param {string} variantName - The variant name (e.g., "Queen")
 * @returns {string} The formatted label
 */
export function formatPriceLabel(displayFormat, variantName) {
  if (displayFormat === 'specific' && variantName) {
    return `${variantName}:`;
  }
  return 'From:';
}
